import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import { buildRemainderLettersFromItems, printRemainderLetters } from '../../../utils/printUtility';

const formatDate = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`;
};

const ReminderLetterTab = () => {
  const [ledgerFrom, setLedgerFrom] = useState('');
  const [ledgerTo, setLedgerTo] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().slice(0, 10));
  const [finalDate, setFinalDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState('');
  const [includedIds, setIncludedIds] = useState(new Set());
  const [confirmed, setConfirmed] = useState(null);

  const fetchLedgers = () => {
    if (!ledgerFrom || !ledgerTo || !fromDate || !toDate || !currentDate) {
      setError('Ledger From, Ledger To, From Date, To Date and Current Date are required');
      return;
    }
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/reminder-letter/ledgers`, {
      params: { ledgerFrom: ledgerFrom.trim(), ledgerTo: ledgerTo.trim(), fromDate, toDate, currentDate },
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        const d = res.data.data;
        setData(d);
        setFinalDate(d.finalDate ? new Date(d.finalDate).toISOString().slice(0, 10) : '');
        setIncludedIds(new Set((d.ledgers || []).map((l) => l._id)));
        setConfirmed(null);
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  const toggleLedger = (id) => {
    setIncludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmLetter = () => {
    if (!data) return;
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.post(
      `${baseURL}/auction/reminder-letter/confirm`,
      {
        ledgerFrom: data.ledgerFrom,
        ledgerTo: data.ledgerTo,
        fromDate: data.fromDate,
        toDate: data.toDate,
        currentDate: data.currentDate || currentDate,
        finalDate: finalDate || data.finalDate,
        includeIds: Array.from(includedIds)
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((res) => setConfirmed(res.data.data))
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  const handlePrint = async () => {
    if (!confirmed?.items?.length) return;
    setError('');
    setPrintLoading(true);
    try {
      const letters = buildRemainderLettersFromItems(confirmed.items, {
        noticeDate: confirmed.noticeDate || confirmed.currentDate || currentDate,
        finalDueDate: confirmed.finalDate
      });
      await printRemainderLetters(letters);
    } catch (err) {
      setError(err.message || 'Failed to print reminder letters');
    } finally {
      setPrintLoading(false);
    }
  };

  const letterCount = confirmed?.items?.length
    ? buildRemainderLettersFromItems(confirmed.items).length
    : 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Reminder Letter</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Customer code range and pledge date range. Prints formal PRE-AUCTION NOTICE (one letter per customer). Paper size from Master → Printer Settings → Remainder Letter.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField label="Ledger A/C From" value={ledgerFrom} onChange={(e) => setLedgerFrom(e.target.value)} size="small" />
        <TextField label="Ledger A/C To" value={ledgerTo} onChange={(e) => setLedgerTo(e.target.value)} size="small" />
        <TextField label="From Date (pledge)" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="To Date (pledge)" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <TextField label="Current Date" type="date" value={currentDate} onChange={(e) => setCurrentDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
        <Button variant="contained" onClick={fetchLedgers} disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Load Ledgers'}</Button>
      </Box>

      {data && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField label="Final Date" type="date" value={finalDate} onChange={(e) => setFinalDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">Uncheck any pledge to exclude from letters</Typography>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 320, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Include</TableCell>
                  <TableCell>Ticket No</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Pledge Date</TableCell>
                  <TableCell>Principal</TableCell>
                  <TableCell>Total Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.ledgers || []).map((row) => (
                  <TableRow key={row._id}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={includedIds.has(row._id)} onChange={() => toggleLedger(row._id)} />
                    </TableCell>
                    <TableCell>{row.ticketNo}</TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>{formatDate(row.pledgeDate)}</TableCell>
                    <TableCell>{row.principalAmount}</TableCell>
                    <TableCell>{row.totalPaid}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button variant="contained" onClick={confirmLetter} disabled={loading || includedIds.size === 0}>
            {loading ? <CircularProgress size={24} /> : 'Confirm for Print'}
          </Button>
        </>
      )}

      {confirmed && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1">
            Ready to print — {confirmed.items?.length || 0} pledge(s), {letterCount} letter(s)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Final Due Date: {formatDate(confirmed.finalDate)} | Letter Date: {formatDate(confirmed.noticeDate || confirmed.currentDate)}
          </Typography>
          <TableContainer sx={{ mt: 1, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket No</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Pledge Date</TableCell>
                  <TableCell>Principal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(confirmed.items || []).map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.ticketNo}</TableCell>
                    <TableCell>{item.customerName}</TableCell>
                    <TableCell>{item.address}</TableCell>
                    <TableCell>{formatDate(item.pledgeDate)}</TableCell>
                    <TableCell>{item.principalAmount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            startIcon={printLoading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
            onClick={handlePrint}
            disabled={printLoading || !confirmed.items?.length}
          >
            {printLoading ? 'Printing…' : `Print Reminder Letter${letterCount > 1 ? 's' : ''}`}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default ReminderLetterTab;
